/**
 * What a file's card shows, decided once for the preview and the wall's
 * height estimate: a PDF's first page (its stored thumbnail, else a calm
 * paper sheet with the title) or the calm file card for other files.
 */
import { asRecord, asString } from "@/components/shapes/shared/plain-record";
import { activeLocaleTag, translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import {
  posterAspect,
  posterUrl,
} from "@/components/shapes/html-frame/html-poster-theme";
import type { FrameData } from "@/lib/frame-data";
import { isPdfFile } from "@/lib/pdf-document";
import { previewSource } from "@/lib/frame-preview-source";

/** An A4 sheet, width / height (mockup `.paper`). */
const PAPER_RATIO = 0.707;
/** A page's card ratio is clamped like an HTML cover's, top crop. */
const MIN_PAGE_RATIO = 0.5;
const MAX_PAGE_RATIO = 1.9;
/** The calm file card (mockup `.cover`), also the top of its opened window. */
export const FILE_CARD_RATIO = 1.15;

export type FileCardShape =
  | {
      kind: "pdf";
      /** The page-1 thumbnail, else "" (the paper sheet shows). */
      url: string;
      ratio: number;
      pages: number | null;
    }
  | { kind: "file"; ratio: number };

/** The PDF page count stored with the thumbnail, else null. */
function storedPages(thumbnail: unknown): number | null {
  const pages = asRecord(asRecord(asRecord(thumbnail)?.light)?.cover)?.pages;
  return typeof pages === "number" && Number.isInteger(pages) && pages > 0
    ? pages
    : null;
}

/** The card of file `frame`. */
export function fileCardShape(frame: FrameData): FileCardShape {
  const source = previewSource(frame);
  if (!isPdfFile(source)) return { kind: "file", ratio: FILE_CARD_RATIO };
  const thumbnail = frame.metadata?.thumbnail;
  const url = posterUrl(thumbnail, "light");
  return {
    kind: "pdf",
    url,
    ratio: Math.min(
      MAX_PAGE_RATIO,
      Math.max(
        MIN_PAGE_RATIO,
        (url && posterAspect(thumbnail, "light")) || PAPER_RATIO,
      ),
    ),
    pages: storedPages(thumbnail),
  };
}

/** A file's name without its extension, and its extension in capitals. */
export function fileNameParts(source: Record<string, unknown>): {
  stem: string;
  label: string;
} {
  const name = asString(source.name);
  const kind = asString(source.kind);
  const extension = name.includes(".") ? (name.split(".").pop() ?? "") : "";
  return {
    stem: extension ? name.slice(0, -extension.length - 1) : name,
    label: (
      (kind || extension).slice(0, 4) ||
      translate(framesSharedMessages, "frameTypeFile")
    ).toLocaleUpperCase(activeLocaleTag()),
  };
}

/** Whether a file has anything to show; else card and frame say so. */
export function hasFile(source: Record<string, unknown>): boolean {
  return [source.name, source.kind, source.url].some(
    (value) => asString(value) !== "",
  );
}

/** A PDF's title on its cover: its file name without the extension. */
export function pdfTitle(frame: FrameData): string {
  return fileNameParts(previewSource(frame)).stem || frame.title || "PDF";
}
